import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signIn } from 'aws-amplify/auth';
import AuthModeToggle from '../../components/auth/AuthModeToggle.tsx';
import AuthShell from '../../components/auth/AuthShell.tsx';
import PasswordInput from '../../components/auth/PasswordInput.tsx';
import TextInput from '../../components/auth/TextInput.tsx';
import { humanizeAuthError, isUserAlreadyAuthenticated } from '../../auth/errors.ts';
import { readNext } from '../../auth/postAuthRedirect.ts';
import {
  getRecaptchaToken,
  isRecaptchaError,
  MINT_TIMEOUT_MS,
  preloadRecaptcha,
} from '../../auth/recaptcha.ts';
import { reportRecaptchaFailure } from '../../auth/recaptchaReport.ts';
import { readStoredDeviceKey } from '../../auth/rememberedDevice.ts';
import { isFlaggedRoute, routeForNextStep } from '../../auth/signInNextStep.ts';
import { useAuth } from '../../auth/useAuth.ts';
import './SignIn.css';

interface SignInLocationState {
  justVerified?: boolean;
  email?: string;
}

function readState(state: unknown): SignInLocationState {
  if (state && typeof state === 'object') {
    return state;
  }
  return {};
}

export default function SignIn() {
  const navigate = useNavigate();
  const location = useLocation();
  const { status, user, refresh, signOut } = useAuth();
  const incoming = readState(location.state);

  const [email, setEmail] = useState(incoming.email ?? '');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Risk-flagged sign-in (reCAPTCHA low score) — honest guidance instead of
  // a false "incorrect email or password" (task #28).
  const [flagged, setFlagged] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  // Set when the last failure was a client-side reCAPTCHA mint failure, so we
  // offer an explicit "Try again" (the mint self-heals its script cache).
  const [retryable, setRetryable] = useState(false);

  const canSubmit = email.trim().length > 0 && password.length > 0 && !submitting;

  // Warm the reCAPTCHA script on mount so minting is instant at submit.
  // The token itself is still minted at submit time (v3 tokens expire in
  // ~2 min); this just front-loads the network/script cost.
  useEffect(() => {
    preloadRecaptcha();
  }, []);

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!canSubmit) {
      return;
    }
    void attemptSignIn();
  }

  // Extracted so the "Try again" button (shown after a reCAPTCHA mint
  // failure) can re-run the exact same flow without a synthetic form event.
  async function attemptSignIn() {
    setError(null);
    setFlagged(false);
    setRetryable(false);
    setSubmitting(true);
    const startedAt = Date.now();
    try {
      // CUSTOM_WITH_SRP enables the User Pool's custom challenge
      // chain — password validates via SRP, then the magic-link
      // approval custom challenge fires for new devices. Remembered
      // devices: we surface Amplify's stored deviceKey through
      // clientMetadata so DefineAuthChallenge can short-circuit
      // straight to tokens (Cognito doesn't do this automatically for
      // CUSTOM_AUTH flows).
      // Mint a fresh reCAPTCHA v3 token unconditionally — even on
      // remembered devices — so the server-side DefineAuthChallenge gate
      // can verify every sign-in before issuing tokens. A mint failure
      // (script blocked/offline) throws RecaptchaError, caught below.
      const recaptchaToken = await getRecaptchaToken('login');
      const deviceKey = readStoredDeviceKey();
      const output = await signIn({
        username: email,
        password,
        options: {
          authFlowType: 'CUSTOM_WITH_SRP',
          clientMetadata: {
            ...(deviceKey === undefined ? {} : { deviceKey }),
            recaptchaToken,
          },
        },
      });
      const next = routeForNextStep(output);
      if (isFlaggedRoute(next)) {
        setFlagged(true);
        return;
      }
      if (!next) {
        setError(
          `Unhandled sign-in step: ${output.nextStep.signInStep}. Please contact admin@oculusmind.ai.`,
        );
        return;
      }
      // When sign-in completes directly (device-remembered path),
      // refresh AuthContext synchronously so RequireAuth on /app sees
      // status='authenticated' on the very next render. Without this,
      // Amplify Hub fires 'signedIn' asynchronously and the route
      // guard bounces back to /auth/signin in the race window.
      if (output.isSignedIn) {
        await refresh();
      }
      // A marketing CTA may have asked us to return the user to a specific
      // page (e.g. /agent/uno) via `?next=`. On the device-remembered DONE
      // path we redirect there directly; on the new-device custom-challenge
      // path we forward it into check-email's state so it survives the
      // magic-link wait and the final navigate happens there.
      const requestedNext = readNext(location.search);
      if (next.path === '/auth/check-email') {
        navigate(next.path, {
          state: { ...next.state, next: requestedNext ?? undefined },
          replace: true,
        });
      } else {
        navigate(requestedNext ?? next.path, { state: next.state, replace: true });
      }
    } catch (err) {
      // Safety net: if a session already exists Amplify throws
      // UserAlreadyAuthenticatedException instead of signing in. Rather
      // than surface a confusing error, re-sync AuthContext so the
      // "already signed in" panel below takes over on the next render.
      if (isUserAlreadyAuthenticated(err)) {
        await refresh();
        return;
      }
      // A client-side reCAPTCHA mint failure never reached the server, so
      // report it (fire-and-forget) for server-side visibility, and offer a
      // retry — the mint self-heals its script cache, so a re-attempt can
      // succeed after a transient stall.
      if (isRecaptchaError(err)) {
        reportRecaptchaFailure(err, 'signin', Date.now() - startedAt);
        setRetryable(true);
      }
      setError(humanizeAuthError(err));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSignOut(redirectToSignedOut: boolean) {
    setSigningOut(true);
    try {
      await signOut();
    } finally {
      setSigningOut(false);
    }
    if (redirectToSignedOut) {
      navigate('/auth/signed-out', { replace: true });
    }
    // Otherwise stay on /auth/signin: status is now 'unauthenticated',
    // so the form renders for a fresh sign-in.
  }

  // Already authenticated: don't try to sign in again (Amplify would
  // throw UserAlreadyAuthenticatedException). Offer to continue, switch
  // accounts, or sign out.
  if (status === 'authenticated' && user) {
    const displayName = user.preferredUsername ?? user.email;
    return (
      <AuthShell
        title="Already signed in"
        subtitle="You're signed in to OculusMind.AI on this device."
      >
        <div className="om-signin__signed-in">
          <p className="om-signin__identity">
            Signed in as <strong>{displayName}</strong>
            {user.preferredUsername ? <span className="om-signin__email">{user.email}</span> : null}
          </p>

          <button
            type="button"
            className="om-signin__submit"
            onClick={() => navigate('/app', { replace: true })}
            disabled={signingOut}
          >
            Continue
          </button>
          <button
            type="button"
            className="om-signin__secondary"
            onClick={() => void handleSignOut(false)}
            disabled={signingOut}
          >
            {signingOut ? 'Signing out…' : 'Sign in with a different account'}
          </button>
          <button
            type="button"
            className="om-signin__secondary"
            onClick={() => void handleSignOut(true)}
            disabled={signingOut}
          >
            Sign out
          </button>
        </div>
      </AuthShell>
    );
  }

  // Session check still in flight: show a neutral placeholder so the
  // form doesn't flash before we know whether a user is signed in.
  if (status === 'initializing') {
    return (
      <AuthShell title="Sign in">
        <p className="om-signin__loading" role="status">
          Checking your session…
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Sign in"
      topNav={<AuthModeToggle current="signin" />}
      subtitle={
        incoming.justVerified
          ? 'Account created. Sign in to continue.'
          : "We'll email a magic link to approve sign-in from a new device. Remembered devices skip this step."
      }
      footer={
        <span>
          <Link to="/auth/forgot-password">Forgot password?</Link>
        </span>
      }
    >
      <form className="om-signin" onSubmit={handleSubmit} noValidate>
        <TextInput
          label="Email"
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
          disabled={submitting}
        />
        <PasswordInput
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
          disabled={submitting}
        />

        {flagged ? (
          <div className="om-signin__flagged" role="alert">
            <p className="om-signin__flagged-title">
              This sign-in attempt was flagged as potential bot traffic.
            </p>
            <p>
              Your email and password were <strong>not</strong> the problem —
              our automated-traffic screen scored this attempt too low to
              continue. This can happen on shared company networks, VPNs, or
              right after several rapid attempts.
            </p>
            <ul>
              <li>Try again in a minute or two.</li>
              <li>If you can, switch off a VPN or shared corporate network.</li>
              <li>
                Building an automated integration? Use the{' '}
                <Link to="/integrations">Integrations page</Link> to mint an
                API token instead of browser sign-in.
              </li>
            </ul>
          </div>
        ) : null}
        {error ? (
          <p className="om-signin__error" role="alert">
            {error}
          </p>
        ) : null}
        {retryable ? (
          <button
            type="button"
            className="om-signin__secondary"
            onClick={() => void attemptSignIn()}
            disabled={submitting}
          >
            {submitting ? 'Retrying…' : 'Try again'}
          </button>
        ) : null}

        <div className="om-signin__submit-wrap">
          <button
            type="submit"
            className="om-signin__submit"
            disabled={!canSubmit}
            aria-disabled={!canSubmit}
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
          {/* While the request is in flight, a thin bar fills over the
              reCAPTCHA timeout window so the user can see the screen is
              working (and roughly when it will give up) instead of facing a
              frozen button. Decorative — the button text + aria-disabled
              already announce state — so it's hidden from assistive tech. */}
          {submitting ? (
            <div
              className="om-signin__progress"
              style={
                {
                  '--om-signin-progress-duration': `${MINT_TIMEOUT_MS}ms`,
                } as React.CSSProperties
              }
              aria-hidden="true"
            />
          ) : null}
        </div>

        {/* reCAPTCHA v3 branding (Option B): badge hidden via CSS, so
            Google's terms require this disclosure on the page. */}
        <p className="om-signin__recaptcha">
          This site is protected by reCAPTCHA and the Google{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>{' '}
          and{' '}
          <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{' '}
          apply.
        </p>
      </form>
    </AuthShell>
  );
}
